import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  X,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchTrajectories, type TrajectoryListItem } from '@/services/api';
import { fetchHITLStats, type HITLStats } from '@/services/hitlApi';

interface GroupedTrajectories {
  [date: string]: TrajectoryListItem[];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TrajectoryList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trajectories, setTrajectories] = useState<TrajectoryListItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });

  // Filters
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agent') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [searchQuery, setSearchQuery] = useState('');

  // HITL stats
  const [hitlStats, setHitlStats] = useState<HITLStats | null>(null);

  // Load HITL stats
  const loadHitlStats = async () => {
    try {
      const stats = await fetchHITLStats();
      setHitlStats(stats);
    } catch (err) {
      console.error('Failed to load HITL stats:', err);
    }
  };

  useEffect(() => {
    loadHitlStats();
  }, []);

  // Load trajectories
  const loadTrajectories = async () => {
    try {
      setError(null);
      setLoading(true);

      const page = parseInt(searchParams.get('page') || '1');
      const result = await fetchTrajectories({
        page,
        per_page: 20,
        agent_name: agentFilter || undefined,
        status: statusFilter || undefined,
      });

      setTrajectories(result.trajectories);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Failed to load trajectories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trajectories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrajectories();
  }, [searchParams, agentFilter, statusFilter]);

  // Group trajectories by date
  const groupedTrajectories = useMemo<GroupedTrajectories>(() => {
    const groups: GroupedTrajectories = {};

    // Filter by search query
    const filtered = searchQuery
      ? trajectories.filter(t =>
          t.report_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.agent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.id.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : trajectories;

    filtered.forEach((trajectory) => {
      const dateKey = new Date(trajectory.created_at).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(trajectory);
    });

    return groups;
  }, [trajectories, searchQuery]);

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      prev.set('page', newPage.toString());
      return prev;
    });
  };

  const handleClearFilters = () => {
    setAgentFilter('');
    setStatusFilter('');
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters = agentFilter || statusFilter || searchQuery;

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-8">
        <Alert variant="destructive" className="max-w-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading trajectories</AlertTitle>
          <AlertDescription className="mt-2">
            {error}
            <div className="mt-4">
              <Button variant="outline" onClick={loadTrajectories}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold">Runs</h1>
              {hitlStats && hitlStats.pending_count > 0 && (
                <Badge
                  variant="outline"
                  className="bg-yellow-500/10 text-yellow-600 border-yellow-500/50 flex items-center gap-1"
                >
                  <MessageSquare className="h-3 w-3" />
                  {hitlStats.pending_count} HITL Pending
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={loadTrajectories} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search trajectories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="hitl_pending">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3 text-yellow-500" />
                    HITL Pending
                  </span>
                </SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder="Agent name..."
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="w-40"
            />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : trajectories.length === 0 ? (
          <div className="text-center py-12">
            <Filter className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No trajectories found</h3>
            <p className="text-muted-foreground mt-1">
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'No trajectories have been recorded yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedTrajectories).map(([dateKey, items]) => (
              <div key={dateKey}>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">
                  {formatDate(items[0].created_at)}
                </h2>
                <div className="grid gap-3">
                  {items.map((trajectory) => {
                    const statusVariant = {
                      completed: 'default',
                      running: 'secondary',
                      failed: 'destructive',
                      cancelled: 'outline',
                    }[trajectory.status] as 'default' | 'secondary' | 'destructive' | 'outline';

                    return (
                      <Card
                        key={trajectory.id}
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => navigate(`/trajectory/${trajectory.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium truncate">
                                  {trajectory.report_title || 'Untitled Trajectory'}
                                </h3>
                                <Badge variant={statusVariant} className="shrink-0">
                                  {trajectory.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>{formatTime(trajectory.created_at)}</span>
                                {trajectory.agent_name && (
                                  <span className="truncate">{trajectory.agent_name}</span>
                                )}
                                <span>{trajectory.total_events} events</span>
                                <span className="font-mono text-xs">
                                  {trajectory.id.slice(0, 8)}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrajectoryList;
